import { z } from "zod";
import { AdPosition, AdStatus, AdsType, LinkType } from "./enums.js";
/** Y-m-d date string (what the DB stores; connection uses dateStrings:true — §5.1). */
const ymd = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "expected Y-m-d");
/**
 * Create/edit Default Advertisement — the §6.1 form contract.
 *
 * NOTE on org targeting (Decision 1): internal users (`utype==1`) are cross-org
 * super-admins for authoring, so the legacy `totalOrgIds` multi-org field is
 * re-added as `orgIds`. The ad fans out one placement row per selected org ×
 * product-type. The server still never trusts these for READ scoping (that comes
 * from the validated `:orgId` path); for WRITES it decodes each id and validates
 * it is a real org before inserting. `advertiser_org_id` stays 0 for default ads —
 * the org lives in the placement `fk_org_id`.
 */
export const adWriteSchema = z.object({
    /** frmAdvertisementTitle → advertisement_title */
    title: z.string().trim().min(1).max(255),
    /** frmAdvertisementPosition → advertisement_position (First < Last ordering) */
    position: z.nativeEnum(AdPosition),
    /** frmAdvertisementStartDate → advertisement_start_date (Y-m-d) */
    startDate: ymd,
    /** frmAdvertisementEndDate → advertisement_end_date (Y-m-d) */
    endDate: ymd,
    /** frmAdvertisementStatus → advertisement_status */
    status: z.nativeEnum(AdStatus),
    /** linkType → advertisement_attachment_type */
    linkType: z.nativeEnum(LinkType),
    /** frmAdvertisementURL → advertisement_attachment_value */
    linkValue: z.string().trim().min(1).max(1000),
    /** frmAdsType → expands to placement rows (§6.1) */
    adsType: z.nativeEnum(AdsType),
    /**
     * frmAdvertisementForIndividualArticle → advertisement_for_individual_article.
     * Persisted as 1 ONLY when checked AND adsType ∈ {newsletter, all}; else 0 (§6.1).
     * The server enforces that gate; this is just the user's intent.
     */
    showOnArticlePages: z.boolean().default(false),
    /**
     * advertisement_image — the final stored name `time()."_".originalName` produced
     * by the upload step (§6 filename contract). References an already-uploaded temp.
     */
    image: z.string().min(1),
    /**
     * totalOrgIds (re-added, Decision 1) — the encoded org ids the ad targets. The
     * server decodes each, validates it exists, and writes one placement row per
     * org × product-type (§6.1). At least one org is required.
     */
    orgIds: z.array(z.string().min(1)).min(1, "select at least one organisation"),
});
//# sourceMappingURL=ad.js.map